import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { KeywordImportAttributes, Site } from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import KeywordSuggestions from "../../keywordSuggestions/KeywordSuggestions";
import NotificationBlock from "../../notificationBlock/NotificationBlock";
import Button from "../../ui/button/Button";
import { tabs } from "./NewSite";
import styles from "./NewSiteKeywords.module.scss";

type NewSiteKeywordsProps = {
  setActiveTab: (tab: string) => void;
  site: Site | null | undefined;
  newSiteId: number;
  nextTab?: string;
};

const NewSiteKeywords = ({
  setActiveTab,
  site,
  newSiteId,
  nextTab = tabs[4],
}: NewSiteKeywordsProps) => {
  const navigation = useNavigate();
  const queryClient = useQueryClient();
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const { mutate: createKeywords, isPending: isPendingCreateKeywords } =
    useMutation({
      mutationFn: async (data: KeywordImportAttributes) => {
        return await apiClient.sites.keywordImportsCreate(newSiteId, data);
      },
      onSuccess: () => {
        enqueueSnackbar(`Keywords were added successfully!`, {
          variant: "success",
        });
        queryClient.invalidateQueries({ queryKey: ["keywordSuggestions"] });
        setActiveTab(nextTab);
        navigation(nextTab);
      },
      onError: (error) => {
        // @ts-expect-error from responce
        enqueueSnackbar(error?.error?.error, {
          variant: "error",
          persist: true,
        });
      },
    });
  const submitKeywords = () => {
    if (!selectedKeywords.length) {
      setActiveTab(nextTab);
      navigation(nextTab);

      return;
    }
    const payload = {
      names: selectedKeywords,
      keyword_attributes: {
        country: site?.primary_country || "",
        language: site?.primary_language || "",
      },
    };
    createKeywords(payload);
  };

  return (
    <div>
      <div className={styles.keywordsSectionHeader}>
        <h2 className={styles.title}>Add Keywords</h2>
        <p className={styles.description}>
          Choose your keywords from the suggestions below. After adding your
          site, you will have access to more detailed settings and will be able
          to add, edit and update your keywords.
        </p>
      </div>
      <NotificationBlock
        type="info"
        message={`Note. If your site is new or still under construction, the keyword suggestions may not be accurate. Just click "Continue” we'll add more keywords later.`}
      ></NotificationBlock>
      <KeywordSuggestions
        selectedKeywords={selectedKeywords}
        setSelectedKeywords={setSelectedKeywords}
        siteId={newSiteId}
      />

      <div className={styles.footer}>
        <Button
          outlined
          onClick={() => {
            setActiveTab(tabs[2]);
            navigation(tabs[2]);
          }}
        >
          Back
        </Button>
        <Button
          onClick={submitKeywords}
          type="submit"
          formId="newSiteDetailsForm"
          disabled={isPendingCreateKeywords}
          withSpinner={isPendingCreateKeywords}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
export default NewSiteKeywords;
